import { useMemo } from 'react';

import { Routes } from '~/domains/common/constants/routes';
import { BRANDS, PLATFORMS } from '~/domains/auth/consts';
import type { EO_BRANDS } from '~/domains/auth/consts';
import { reportEvent } from '~/domains/analytics';
import { createQueryString } from '~/domains/common/utils/queryString';

const useEORedirectUrl = () => {
  const destinations = useMemo(
    () => ({
      [BRANDS.RYP]: Routes.authRYP(),
      [BRANDS.OFP]: Routes.authOFP(),
    }),
    []
  );

  const defaultUrlPath = useMemo(
    () => ({
      [BRANDS.RYP]: 'dashboard/my-pools',
      [BRANDS.OFP]: '',
    }),
    []
  );

  const platformDestinations = useMemo(
    () => ({
      [PLATFORMS.IOS]: Routes.authRYPMobile(),
      [PLATFORMS.ANDROID]: Routes.authRYPMobile(),
    }),
    []
  );

  const getEORedirectUrl = (
    destination: keyof typeof EO_BRANDS,
    platform: PLATFORMS,
    sut: string
  ) => {
    if (!destination || !sut) return null;

    // For EO RYP mobile app redirects - only for RYP as of now
    if (destination === BRANDS.RYP && platformDestinations[platform]) {
      const baseDeepLinkURL = platformDestinations[platform];
      const deepLinkURL = new URL(baseDeepLinkURL);

      deepLinkURL.search = createQueryString({
        sut: sut || undefined,
      });

      reportEvent('User > App Auth', {
        brandPlatform: platform,
        brand: destination,
      });

      return deepLinkURL.toString();
    }

    const baseUrl = destinations[destination];
    if (!baseUrl) return null;

    const currentParams = new URLSearchParams(window.location.search);
    const redirectToEO = currentParams.get('redirectToEO')
      ? decodeURIComponent(currentParams.get('redirectToEO'))
      : undefined;
    const newUrlPath = redirectToEO || defaultUrlPath[destination];

    let url: URL;

    try {
      url = new URL(newUrlPath, baseUrl);
    } catch (error) {
      url = new URL('', baseUrl);
    }

    const redirectToEOParams = Object.fromEntries(url.searchParams.entries());

    url.search = createQueryString({
      ...redirectToEOParams,
      sut: sut || undefined,
    });

    return url.toString();
  };

  return { getEORedirectUrl };
};

export default useEORedirectUrl;
